import Blocks from '@components/ArtBoard/Blocks/Blocks'
import { VARIANTS } from '@components/ArtBoard/Node/constants'
import { Tooltip } from '@lib/components'
import { isValidHttpUrl } from '@lib/utils/strings'
import { ProjectActions } from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { artBoardActions } from '@store/reducers/artboard'
import { commitProjectRequest } from '@storeOld/actionCreators'
import { deleteFigmaImage } from '@storeOld/reducers/tree'
import type React from 'react'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { TREE_MATRIX } from '@/constants/constants'
import { DEFAULT_PAGE_NAME } from '@/constants/defaultNames'
import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import type { AppState, NodeFigma, TreeItemVariants } from '@/types'
import type { Color } from '@/types/style'
import { clsJoin } from '@/utils'
import { formatLegacyStorageUrl } from '@/utils/formatLegacyStorageUrl'
import type { NodeClasses } from '../../../../types'
import { ChildrenCorner } from '../../../ChildrenCorner'
import { Handler } from '../../../Handler'
import { ParentCorners } from '../../../ParentCorners'
import { Actions } from './components/Actions'
import { Loading } from './components/Loading'
import { Name } from './components/Name'
import { Refresh } from './components/Refresh'
import { Siblings } from './components/Siblings'
import IconMinus from './icons/IconMinus'
import css from './NodeGallery.module.scss'

type Props = {
  color: Color | string
  id: string
  name: string
  variant: TreeItemVariants
  nodeClasses: NodeClasses
  image: {
    id: string
    imageUrl: string
    thumbnailUrl: string
  } | null
  url: string
  readOnly: boolean
  isChild: boolean
  parentId: string | null
  hasChildren: boolean
  hasChild: boolean
  isCollapsed: boolean
  figma: NodeFigma | null
}

const NodeGallery: React.FC<Props> = (props) => {
  const dispatch = useDispatch()

  const isProjectNeutral = useSelector(
    migrationProjectSelector.getIsProjectNeutral
  )

  const treeMode = useSelector(migrationProjectSelector.getProjectTreeType)

  const [loading, setLoading] = useState(!!(props.image || props.figma))

  const isReadOnly = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )

  const isLoggedIn = useSelector<AppState, boolean>(
    ({ auth }) => auth.authenticated
  )

  const nodeStyles = {
    '--node-color': props.color,
  } as React.CSSProperties

  const nodeClasses = {
    container: clsJoin(
      props.nodeClasses.container,
      (isReadOnly || !isLoggedIn) && css.read_only
    ),
    node: clsJoin(props.nodeClasses.node, css.node),
    body: clsJoin(props.nodeClasses.body, css.body),
    stackGhost: clsJoin(props.nodeClasses.stackGhost),
    image: clsJoin(
      css.image,
      props.variant === VARIANTS.FRAME && css.dashed,
      loading && css.loading
    ),
  }

  const figmaImageURL = props.figma?.preview || ''
  const figmaThumbnailURL = props.figma?.thumbnail || ''

  const onClickPreviewImage = () => {
    if (!props.image && !props.figma) {
      return
    }

    sendGtmEvent(
      GtmTriggerName.IMAGE_VIEW,
      'Click on image to view full-screen'
    )

    dispatch(
      artBoardActions.setPreview(
        props.image ? getLegacyImageUrl(props.image) : figmaImageURL
      )
    )
  }

  const getLegacyImageUrl = (
    image: { imageUrl: string; thumbnailUrl: string },
    isThumb = false
  ) => {
    const url = isThumb ? image.thumbnailUrl : image.imageUrl

    if (!isValidHttpUrl(url)) {
      return formatLegacyStorageUrl(url)
    }

    return url
  }

  const onDelete = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (props.image) {
      sendGtmEvent(
        GtmTriggerName.IMAGE_REMOVE,
        'Click to remove image from page'
      )

      dispatch(
        commitProjectRequest(ProjectActions.nodes.deleteImage({ id: props.id }))
      )
    }

    if (!props.image && props.figma) {
      dispatch(deleteFigmaImage(props.id))
    }
  }

  if (props.image || props.figma) {
    return (
      <div className={nodeClasses.container} style={nodeStyles}>
        {props.isChild && treeMode !== TREE_MATRIX && (
          <ChildrenCorner id={props.id} parentId={props.parentId} />
        )}

        {(props.hasChildren || props.hasChild) &&
          treeMode !== TREE_MATRIX &&
          !props.isCollapsed && <ParentCorners />}

        {props.variant === VARIANTS.STACK && (
          <div className={nodeClasses.stackGhost} />
        )}

        <div
          className={clsJoin(nodeClasses.image, 'node-image')}
          onClick={onClickPreviewImage}
        >
          <img
            src={
              props.image
                ? getLegacyImageUrl(props.image, true)
                : figmaThumbnailURL
            }
            className={css.img}
            alt={props.name || DEFAULT_PAGE_NAME}
            onLoad={() => setLoading(false)}
          />

          {!props.readOnly &&
            props.figma?.figmaUrl &&
            !isReadOnly &&
            isLoggedIn &&
            !loading && (
              <div className={css.refresh}>
                <Refresh nodeId={props.id} url={props.figma.figmaUrl} />
              </div>
            )}

          {!props.readOnly && !isReadOnly && isLoggedIn && !loading && (
            <div className={css.children}>
              <Tooltip content="Remove image" position="top">
                <div className={css.button} onClick={onDelete}>
                  <IconMinus />
                </div>
              </Tooltip>
            </div>
          )}

          {loading && <Loading />}
        </div>

        <Siblings nodeId={props.id} url={props.url} />
      </div>
    )
  }

  if (props.variant === VARIANTS.GHOST) {
    return (
      <div className={nodeClasses.container} style={nodeStyles}>
        {props.isChild && treeMode !== TREE_MATRIX && (
          <ChildrenCorner id={props.id} parentId={props.parentId} />
        )}

        {(props.hasChildren || props.hasChild) &&
          treeMode !== TREE_MATRIX &&
          !props.isCollapsed && <ParentCorners />}

        <div className={css.ghost}>
          <Name variant={props.variant} name={props.name} />
        </div>
      </div>
    )
  }

  return (
    <div className={nodeClasses.container} style={nodeStyles}>
      {props.isChild && treeMode !== TREE_MATRIX && (
        <ChildrenCorner id={props.id} parentId={props.parentId} />
      )}

      {(props.hasChildren || props.hasChild) &&
        treeMode !== TREE_MATRIX &&
        !props.isCollapsed && <ParentCorners />}

      {props.variant === VARIANTS.STACK && (
        <div className={props.nodeClasses.stackGhost} />
      )}

      <div className={nodeClasses.node}>
        <div>
          {(props.variant === VARIANTS.DEFAULT ||
            props.variant === VARIANTS.STACK) &&
            !isProjectNeutral && (
              <Handler
                canDrag={false}
                isSelected={false}
                variant={props.variant}
              />
            )}

          <Name
            variant={props.variant}
            name={props.name}
            isProjectNeutral={isProjectNeutral}
          />
        </div>

        <div className={nodeClasses.body}>
          {!isReadOnly && isLoggedIn && (
            <div className={css.actions}>
              <Actions id={props.id} readOnly={props.readOnly} />
            </div>
          )}

          <div className={css.blocks}>
            <Blocks nodeId={props.id} />
          </div>
        </div>
      </div>

      <Siblings nodeId={props.id} url={props.url} />
    </div>
  )
}

export default NodeGallery
